/**
 * M8-P3 · "View as <user>" — the browser half.
 *
 * **This feature is ours, not monday's** (`specs/M8-P3-view-as-user.md` §Deviations): monday
 * has no impersonation, so there is no capture to copy and nothing here claims measured
 * geometry. What it does claim is three behaviours:
 *
 *  1. While a view is open, **a persistent bar sits above the top bar on every screen**, on a
 *     warning tone, above every overlay (`--ez-layer-impersonation`), with an `Exit view`
 *     button. It is never inside the sidebar and is never covered.
 *  2. **Starting a view resets the browser completely.** Nothing read as the superadmin may be
 *     painted for the target: the TanStack cache is cleared, the socket is dropped, and the
 *     document is reloaded so every in-memory store is rebuilt under the new session. The same
 *     on the way out.
 *  3. **A refused write shows the api's own sentence**, which arrives through the ordinary
 *     `MutationCache.onError` → toast path (`app/errors.ts`); nothing special is needed here,
 *     and that is the point — every write in the product is covered, not a list of them.
 *
 * The components are split into a **pure view** (props only, so `/__ui/impersonation` and the
 * verifier can render them with no session) and a thin connected wrapper.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { disconnectSocket } from '../../lib/realtime/socket';
import { useTRPCClient } from '../../lib/trpc';
import { useBootstrap } from '../shell/bootstrap';
import './impersonation.css';

/** What `shell.bootstrap` answers while a view is open; `null` on every ordinary session. */
export interface ImpersonationState {
  impersonator: { id: string; name: string };
  target: { id: string; name: string; role: string };
  expiresAt: string;
  viewOnly: boolean;
}

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'super admin',
  admin: 'admin',
  member: 'member',
  viewer: 'viewer',
  guest: 'guest',
};

/* ────────────────────────────────────────────────────────────────── the bar */

export function ImpersonationBannerView(props: {
  state: ImpersonationState;
  exiting?: boolean;
  onExit: () => void;
}): React.ReactElement {
  const { target, impersonator, expiresAt } = props.state;
  return (
    <div
      className="ez-impersonation"
      data-testid="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <span className="ez-impersonation__eye" aria-hidden="true">
        👁
      </span>
      <span className="ez-impersonation__text">
        Viewing as <span className="ez-impersonation__name">{target.name}</span>{' '}
        <span className="ez-impersonation__meta">
          ({ROLE_LABEL[target.role] ?? target.role}) · view only
        </span>
      </span>
      <button
        type="button"
        className="ez-impersonation__exit"
        data-testid="impersonation-exit"
        disabled={props.exiting === true}
        onClick={props.onExit}
        title={`Opened by ${impersonator.name}. Ends automatically at ${new Date(expiresAt).toLocaleTimeString()}.`}
      >
        {props.exiting === true ? 'Exiting…' : 'Exit view'}
      </button>
    </div>
  );
}

/**
 * Mounted once, as the first child of `.ez-app`. Renders nothing at all on an ordinary
 * session — the bar exists only while the server says a view is open, so a stale client can
 * never draw one and a real one can never be missed.
 */
export function ImpersonationBanner(): React.ReactElement | null {
  const bootstrap = useBootstrap();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const [exiting, setExiting] = useState(false);

  const state = (bootstrap.data as { impersonation?: ImpersonationState | null } | undefined)
    ?.impersonation;
  if (!state) return null;

  const exit = (): void => {
    if (exiting) return;
    setExiting(true);
    void (async () => {
      try {
        await (
          client as unknown as {
            admin: { impersonation: { stop: { mutate(input?: undefined): Promise<unknown> } } };
          }
        ).admin.impersonation.stop.mutate(undefined);
      } catch {
        /*
         * The view is over either way: a failed exit is a dead or already-ended session, and
         * leaving somebody inside a view they asked to leave is the one outcome that is not
         * acceptable. The reload below lands them on `/login` if the cookie really is gone.
         */
      }
      await leaveTheView(queryClient, '/admin/users');
    })();
  };

  return <ImpersonationBannerView state={state} exiting={exiting} onExit={exit} />;
}

/* ───────────────────────────────────────────── the confirmation dialog (§2) */

export interface ViewAsCandidate {
  id: string;
  name: string;
  email: string;
  role: string;
  status?: string;
}

export function ViewAsDialogView(props: {
  user: ViewAsCandidate;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const { user } = props;
  return (
    <div
      className="ez-view-as"
      role="dialog"
      aria-modal="true"
      aria-labelledby="view-as-title"
      data-testid="view-as-dialog"
    >
      <h2 className="ez-view-as__title" id="view-as-title">
        View the product as {user.name}?
      </h2>
      <p className="ez-view-as__body">
        You will see exactly what {user.name} ({ROLE_LABEL[user.role] ?? user.role},{' '}
        {user.email}) sees — their boards, their sidebar, their notifications and nothing more.
      </p>
      <ul className="ez-view-as__list">
        <li>
          <strong>View only.</strong> Every change is refused while the view is open, including
          their own settings.
        </li>
        <li>They are not told, and they do not appear online — nothing is written as them.</li>
        <li>It ends when you click <strong>Exit view</strong>, and after two hours regardless.</li>
        <li>Starting and ending the view are recorded in the audit log.</li>
      </ul>
      {props.error ? (
        <p className="ez-view-as__error" role="alert" data-testid="view-as-error">
          {props.error}
        </p>
      ) : null}
      <div className="ez-view-as__footer">
        <button
          type="button"
          className="ez-view-as__cancel"
          data-testid="view-as-cancel"
          onClick={props.onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ez-view-as__confirm"
          data-testid="view-as-confirm"
          disabled={props.pending === true}
          onClick={props.onConfirm}
        >
          {props.pending === true ? 'Starting…' : `View as ${user.name}`}
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────────────────── Security ▸ Active impersonations (§7) */

export interface ActiveImpersonationRow {
  sessionId: string;
  startedAt: string;
  expiresAt: string;
  ip: string | null;
  impersonator: { id: string; name: string };
  target: { id: string; name: string; email: string; role: string };
}

export function ActiveImpersonationsView(props: {
  rows: ActiveImpersonationRow[];
  onEnd: (sessionId: string) => void;
}): React.ReactElement {
  if (props.rows.length === 0) {
    return (
      <div className="ez-impersonation-active" data-testid="impersonation-active">
        <div className="ez-impersonation-active__empty">Nobody is viewing as another user.</div>
      </div>
    );
  }
  return (
    <div className="ez-impersonation-active" data-testid="impersonation-active">
      {props.rows.map((row) => (
        <div className="ez-impersonation-active__row" key={row.sessionId}>
          <span className="ez-impersonation-active__who">
            <strong>{row.impersonator.name}</strong> is viewing as <strong>{row.target.name}</strong>{' '}
            ({row.target.email})
          </span>
          <span className="ez-impersonation-active__when">
            since {new Date(row.startedAt).toLocaleTimeString()}
            {row.ip ? ` · ${row.ip}` : ''}
          </span>
          <button
            type="button"
            className="ez-view-as__cancel"
            data-testid={`impersonation-end-${row.sessionId}`}
            onClick={() => props.onEnd(row.sessionId)}
          >
            End
          </button>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the transition */

/**
 * The hard reset that makes a view honest, in both directions.
 *
 * A "View as" is a **different session**, so every cached answer, every Zustand store, every
 * open socket and every optimistic write from before it belongs to somebody else. Anything
 * short of a document load leaves one of them behind, and one stale board in the sidebar is
 * enough to make the whole exercise worthless — the superadmin would be checking a mixture of
 * two people's permissions and could not tell which.
 *
 * `location.replace`, never `assign`: the pre-view page must not be one Back away.
 */
export async function leaveTheView(
  /**
   * Optional: the document load below is itself a complete reset, so a caller that has no
   * `QueryClientProvider` above it (the admin screens render standalone in their own unit
   * tests and in `/__ui/admin`) passes `null` and loses nothing.
   */
  queryClient: { cancelQueries(): Promise<void>; clear(): void } | null,
  destination: string,
  redirect: (path: string) => void = (path) => {
    if (typeof window !== 'undefined') window.location.replace(path);
  },
): Promise<void> {
  try {
    disconnectSocket();
  } catch {
    /* no socket was ever opened on this page */
  }
  try {
    await queryClient?.cancelQueries();
  } catch {
    /* cancelling is best effort; the clear below is what matters */
  }
  queryClient?.clear();
  redirect(destination);
}
